import React, { useState } from 'react'

import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js'
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'

import { COGNITO_AUTHORITY, COGNITO_CLIENT_ID, COGNITO_POOL_ID } from '../utils/Constants'

const SignIn = () => {
  const [email, setEmail] = useState('')
  const isTimeout = new URLSearchParams(window.location.search).has('returnTo')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  /* eslint-disable no-unused-vars */
  const [step, setStep] = useState('signIn') // 'signIn' | 'newPassword'
  /* eslint-enable no-unused-vars */
  const [newPassword, setNewPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [cognitoUser, setCognitoUser] = useState(null)
  const [userAttributes, setUserAttributes] = useState(null)

  const handleAuthSuccess = result => {
    const idToken = result.getIdToken().getJwtToken()
    const accessToken = result.getAccessToken().getJwtToken()
    const refreshToken = result.getRefreshToken().getToken()
    const expiration = result.getAccessToken().getExpiration()

    const user = new User({
      id_token: idToken,
      access_token: accessToken,
      refresh_token: refreshToken,
      token_type: 'Bearer',
      scope: 'openid profile email',
      profile: result.getIdToken().payload,
      expires_at: expiration,
    })

    // Create a temporary UserManager to store the user in the same storage as the main app
    const userManager = new UserManager({
      authority: COGNITO_AUTHORITY,
      client_id: COGNITO_CLIENT_ID,
      redirect_uri: window.location.origin,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    })

    userManager
      .storeUser(user)
      .then(() => {
        const returnTo = new URLSearchParams(window.location.search).get('returnTo')
        let targetUrl = '/'
        if (returnTo) {
          const decoded = decodeURIComponent(returnTo)
          if (decoded.startsWith('/') && !decoded.startsWith('//')) {
            targetUrl = decoded
          }
        }
        window.location.replace(targetUrl)
      })
      .catch(e => {
        setError('Failed to store session')
        setLoading(false)
      })
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const authenticationData = {
      Username: email,
      Password: password,
    }
    const authenticationDetails = new AuthenticationDetails(authenticationData)
    const poolData = {
      UserPoolId: COGNITO_POOL_ID,
      ClientId: COGNITO_CLIENT_ID,
    }
    const userPool = new CognitoUserPool(poolData)
    const userData = {
      Username: email,
      Pool: userPool,
    }
    const cognitoUserInstance = new CognitoUser(userData)

    cognitoUserInstance.authenticateUser(authenticationDetails, {
      onSuccess: handleAuthSuccess,

      onFailure: function (err) {
        let msg = err.message || 'Sign in failed'
        if (msg.includes('invalid_grant') || msg.includes('Incorrect username or password')) {
          msg = 'Incorrect username or password.'
        }
        setError(msg)
        setLoading(false)
      },

      newPasswordRequired: function (attributes, requiredAttributes) {
        setCognitoUser(cognitoUserInstance)
        setUserAttributes(attributes)
        setStep('newPassword')
        setLoading(false)
        setError(null)
      },
    })
  }

  const handleNewPasswordSubmit = async e => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const attributesToSubmit = { ...userAttributes }
    delete attributesToSubmit.email_verified
    delete attributesToSubmit.email

    cognitoUser.completeNewPasswordChallenge(newPassword.trim(), attributesToSubmit, {
      onSuccess: handleAuthSuccess,
      onFailure: function (err) {
        setError(err.message || 'Password change failed')
        setLoading(false)
      },
    })
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-4">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <div className="text-center mb-4">
                <img
                  src="/GuideSmart_logo-789x221.png"
                  alt="GuideSmart Logo"
                  className="img-fluid mb-3"
                  style={{ maxHeight: '80px' }}
                />
                <h3 className="mb-1">{step === 'newPassword' ? 'Set New Password' : 'Sign In'}</h3>
                <p className={isTimeout && step !== 'newPassword' ? 'text-danger font-weight-bold' : 'text-muted'}>
                  {step === 'newPassword'
                    ? 'Please set a permanent password'
                    : isTimeout
                    ? 'Your session has expired. Please sign back in.'
                    : 'Welcome back to GuideSmart'}
                </p>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {step === 'newPassword' ? (
                <form onSubmit={handleNewPasswordSubmit}>
                  <div className="form-group mb-4">
                    <label htmlFor="newPassword">New Password</label>
                    <div className="input-group">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        className="form-control"
                        id="newPassword"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        required
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowNewPassword(v => !v)}
                          aria-pressed={showNewPassword}
                          aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                        >
                          <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="btn btn-success btn-block w-100" disabled={loading}>
                    {loading ? 'Setting Password...' : 'Set Password'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-group mb-3">
                    <label htmlFor="email">Email address</label>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      placeholder="Enter email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group mb-4">
                    <label htmlFor="password">Password</label>
                    <div className="input-group">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="form-control"
                        id="password"
                        placeholder="Password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        required
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword(v => !v)}
                          aria-pressed={showPassword}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                    <div className="text-right mt-2">
                      <a href="/forgot-password" style={{ fontSize: '0.9rem' }}>
                        Forgot password?
                      </a>
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary btn-block w-100" disabled={loading}>
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignIn
